import React, { useState, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaFileExcel, 
  FaCalendarAlt,
  FaDownload,
  FaExclamationTriangle,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa'
import { Card, Row, Col, Form, Button, Table } from 'react-bootstrap'
import { searchPA } from '../../context/users/UserActions'
import './SessionReports.css'

// eslint-disable-next-line
export default function SessionReports() {
	// Función para obtener fechas por defecto (7 días atrás y 1 día adelante)
	const getDefaultDates = () => {
		const today = new Date()
		const sevenDaysAgo = new Date(today)
		sevenDaysAgo.setDate(today.getDate() - 7)
		const tomorrow = new Date(today)
		tomorrow.setDate(today.getDate() + 1)
		
		return {
			startDate: sevenDaysAgo.toISOString().split('T')[0],
			endDate: tomorrow.toISOString().split('T')[0]
		}
	}

	const defaultDates = getDefaultDates()
	const [startDate, setStartDate] = useState(defaultDates.startDate)
	const [endDate, setEndDate] = useState(defaultDates.endDate)
	const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
	const [users, setUsers] = useState([])
	const [isLoading, setIsLoading] = useState(false)
	const { authenticatedUser, setAlert } = useUserContext()

	const fetchData = async () => {
		if (!startDate || !endDate) return
		
		setIsLoading(true)
		try {
			const response = await searchPA({ 
				path: 'sessionReport', 
				page, 
				startDate, 
				endDate, 
				token: authenticatedUser.value 
			})
			
			if (response.error || !Array.isArray(response.data)) {
				setAlert({
					message: response.message || 'Error al cargar los datos. Si persiste el error, por favor contacte a soporte.',
					msgType: 'warning'
				})
			} else {
				setUsers(response.data)
				setHasMore(response.paging.hasMore)
			}
		} catch (error) {
			setAlert({
				message: 'Error al cargar los datos. Por favor, inténtalo de nuevo.',
				msgType: 'error'
			})
		} finally {
			setIsLoading(false)
		}
	}

	const handleChangeStartDate = (e) => {
		setStartDate(e.target.value)
	}

	const handleChangeEndDate = (e) => {
		setEndDate(e.target.value)
	}

	const handleSearch = () => {
		if (!startDate || !endDate) {
			setAlert({
				message: 'Por favor selecciona ambas fechas para generar el reporte.',
				msgType: 'warning'
			})
			return
		}
		setPage(1)
		fetchData()
	}

	const nextPage = () => {
    if(hasMore){
      setPage(page + 1)
    }
  }

  const prevPage = () => {
      if(page > 1 ){
        setPage(page - 1)
      }
  }

	window.scrollTo({
		top: 0,
		behavior: 'auto',
	})

	useEffect(() => {
		if (startDate && endDate) {
			fetchData()
		}
	}, [page])

	// Cargar datos automáticamente al montar el componente con las fechas por defecto
	useEffect(() => {
		fetchData()
	}, []) // Solo se ejecuta una vez al montar

	const handleGenerateReport = async () => {
		if (users.length === 0) {
			setAlert({
				message: 'No hay datos para generar el reporte. Por favor, selecciona un rango de fechas y busca datos primero.',
				msgType: 'warning'
			})
			return
		}

		try {
			const result = users.map((user) => ({
				'User ID': user.userId,
				'Nombre': user.firstName,
				'Apellido': user.lastName,
				'Mail': user.userEmail,
				'País': user.country,
				'Fecha de Registro': user.registrationDate,
				'Plan': user.plan,
				'Cantidad de Sesiones': user.noSessions,
				'Invitados Acumulados': user.noGuest
			}))

			const worksheet = XLSX.utils.json_to_sheet(result)
			const workbook = XLSX.utils.book_new()
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte')
			
			const fileName = `Reporte_Sesiones_${startDate}_${endDate}.xlsx`
			XLSX.writeFile(workbook, fileName, { compression: true })
			
			setAlert({
				message: `Reporte generado exitosamente: ${fileName}`,
				msgType: 'success'
			})
		} catch (error) {
			setAlert({
				message: 'Error al generar el reporte. Por favor, inténtalo de nuevo.',
				msgType: 'error'
			})
		}
	}

	return (
		<div className="session-reports-container">
			<div className="container">
				{/* Header Section */}
				<div className="session-reports-header">
					<div className="header-content">
						<div className="header-icon">
							<div className="icon-circle">
								<FaFileExcel className="icon" />
							</div>
						</div>
						<div className="header-text">
							<h1 className="header-title">Reporte de encuestas</h1>
							<p className="header-subtitle">Genera reportes de usuarios por rango de fechas</p>
						</div>
					</div>
				</div>

				{/* Filters Section */}
				<Card className="filters-card">
					<Card.Body>
						<Row>
							<Col lg={3} md={6} sm={12} className="mb-3">
								<Form.Group>
									<Form.Label className="form-label">
										<FaCalendarAlt className="field-icon" />
										Fecha Inicial
									</Form.Label>
									<Form.Control
										type="date"
										value={startDate}
										max={endDate}
										onChange={handleChangeStartDate}
										className="form-control-custom"
									/>
								</Form.Group>
							</Col>
							<Col lg={3} md={6} sm={12} className="mb-3">
								<Form.Group>
									<Form.Label className="form-label">
										<FaCalendarAlt className="field-icon" />
										Fecha Final
									</Form.Label>
									<Form.Control
										type="date"
										value={endDate}
										min={startDate}
										onChange={handleChangeEndDate}
										className="form-control-custom"
									/>
								</Form.Group>
							</Col>
							<Col lg={3} md={6} sm={12} className="mb-3">
								<Form.Group>
									<Form.Label className="form-label">&nbsp;</Form.Label>
									<Button
										variant="primary"
										onClick={handleSearch}
										disabled={!startDate || !endDate || isLoading}
										className="search-btn"
										block
									>
										{isLoading ? (
											<>
												<div className="spinner-border spinner-border-sm me-2" />
												Buscando...
											</>
										) : (
											<>
												<FaCalendarAlt className="me-2" />
												Buscar Datos
											</>
										)}
									</Button>
								</Form.Group>
							</Col>
							<Col lg={3} md={6} sm={12} className="mb-3">
								<Form.Group>
									<Form.Label className="form-label">&nbsp;</Form.Label>
									<Button
										variant="outline-primary"
										onClick={handleGenerateReport}
										disabled={users.length === 0}
										className="download-btn"
										block
									>
										<FaDownload className="me-2" />
										Descargar Excel
									</Button>
								</Form.Group>
							</Col>
						</Row>
					</Card.Body>
				</Card>

				{/* Table Section */}
				<Card className="table-card">
					<Card.Body>
						{isLoading ? (
							<div className="loading-state">
								<FaFileExcel className="loading-icon" />
								<p className="mt-3">Cargando datos...</p>
							</div>
						) : users.length === 0 ? (
							<div className="empty-state">
								<FaFileExcel className="empty-icon" />
								<h4>No se encontraron datos</h4>
								<p>No hay usuarios registrados en el rango de fechas seleccionado. Intenta con un rango diferente.</p>
							</div>
						) : (
							<>
								{/* Desktop Table View */}
								<div className="table-responsive d-none d-lg-block">
									<Table className="session-reports-table">
										<thead>
											<tr>
												<th>User Id</th>
												<th>Nombre</th>
												<th>Apellido</th>
												<th>Mail</th>
												<th>País</th>
												<th>Fecha de Registro</th>
												<th>Plan</th>
												<th>Cantidad de Sesiones</th>
												<th>Invitados Acumulados</th>
											</tr>
										</thead>
										<tbody>
											{users.map((user) => (
												<tr key={user.userId}>
													<td className="user-id">#{user.userId}</td>
													<td className="user-name">{user.firstName || '-'}</td>
													<td className="user-lastname">{user.lastName || '-'}</td>
													<td className="user-email">{user.userEmail || '-'}</td>
													<td className="user-country">{user.country || '-'}</td>
													<td className="user-date">{user.registrationDate || '-'}</td>
													<td className="user-plan">{user.plan || '-'}</td>
													<td className="user-sessions">{user.noSessions || 0}</td>
													<td className="user-guests">{user.noGuest || 0}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</div>

								{/* Tablet Table View */}
								<div className="table-responsive d-none d-md-block d-lg-none">
									<Table className="session-reports-table">
										<thead>
											<tr>
												<th>Nombre</th>
												<th>Apellido</th>
												<th>Mail</th>
												<th>Plan</th>
												<th>Sesiones</th>
												<th>Invitados</th>
											</tr>
										</thead>
										<tbody>
											{users.map((user) => (
												<tr key={user.userId}>
													<td className="user-name">{user.firstName || '-'}</td>
													<td className="user-lastname">{user.lastName || '-'}</td>
													<td className="user-email">{user.userEmail || '-'}</td>
													<td className="user-plan">{user.plan || '-'}</td>
													<td className="user-sessions">{user.noSessions || 0}</td>
													<td className="user-guests">{user.noGuest || 0}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</div>

								{/* Mobile Card View */}
								<div className="session-reports-cards d-block d-md-none">
									{users.map((user) => (
										<Card key={user.userId} className="session-report-card">
											<Card.Body>
												<div className="session-report-card-content">
													<div className="session-report-info">
														<div className="session-report-avatar">
															<div className="avatar-circle">
																<span className="avatar-text">#{user.userId}</span>
															</div>
														</div>
														<div className="session-report-details">
															<div className="session-report-name">
																{user.firstName} {user.lastName}
															</div>
															<div className="session-report-email">
																{user.userEmail}
															</div>
															<div className="session-report-plan">
																Plan: {user.plan || 'N/A'}
															</div>
														</div>
													</div>
													<div className="session-report-stats">
														<div className="stat-item">
															<span className="stat-label">Sesiones:</span>
															<span className="stat-value">{user.noSessions || 0}</span>
														</div>
														<div className="stat-item">
															<span className="stat-label">Invitados:</span>
															<span className="stat-value">{user.noGuest || 0}</span>
														</div>
													</div>
												</div>
											</Card.Body>
										</Card>
									))}
								</div>
							</>
						)}
					</Card.Body>
				</Card>

				{/* Pagination */}
				{users.length > 0 && (
					<div className="pagination-container">
						<div className="pagination-wrapper">
							{/* First Page */}
							<button 
								className={`pagination-nav ${page <= 1 ? 'disabled' : ''}`}
								onClick={() => setPage(1)}
								disabled={page <= 1}
								title="Ir al inicio"
							>
								<FaChevronLeft />
								<FaChevronLeft />
							</button>
							
							{/* Previous Page */}
							<button 
								className={`pagination-nav ${page <= 1 ? 'disabled' : ''}`}
								onClick={prevPage}
								disabled={page <= 1}
								title="Página anterior"
							>
								<FaChevronLeft />
							</button>
							
							{/* Page Numbers */}
							<div className="page-numbers">
								{page > 1 && (
									<button 
										className="page-number"
										onClick={() => setPage(page - 1)}
									>
										{page - 1}
									</button>
								)}
								
								<button className="page-number active">
									{page}
								</button>
								
								{hasMore && (
									<button 
										className="page-number"
										onClick={() => setPage(page + 1)}
									>
										{page + 1}
									</button>
								)}
							</div>
							
							{/* Next Page */}
							<button 
								className={`pagination-nav ${!hasMore ? 'disabled' : ''}`}
								onClick={nextPage}
								disabled={!hasMore}
								title="Página siguiente"
							>
								<FaChevronRight />
							</button>
							
							{/* Last Page */}
							<button 
								className={`pagination-nav ${!hasMore ? 'disabled' : ''}`}
								onClick={() => setPage(page + 1)}
								disabled={!hasMore}
								title="Ir al final"
							>
								<FaChevronRight />
								<FaChevronRight />
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
